import styled, { css, keyframes } from "styled-components";
import { fontFamilies } from "components/shared/tokens/typography";

const COLORS = {
  page: "#f5f7fc",
  white: "#ffffff",
  title: "#293142",
  body: "#59647a",
  muted: "#7d8599",
  line: "#e1e6ef",
  brand: "#ef4f5f",
  slotsFull: "#ef4f5f",
  slotsFullBackground: "#fdecea",
  infoBg: "#f0f3fa",
  warningIcon: "#e87c1e",
  warningIconBg: "#fef3e8",
};

const MOBILE = "@media (max-width: 480px)";
const PAGE_MAX_WIDTH = "760px";

export const PageShell = styled.main`
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  background: ${COLORS.page};
  color: ${COLORS.title};
  font-family: ${fontFamilies.regular};
`;

export const PageFrame = styled.div`
  display: flex;
  width: min(100%, ${PAGE_MAX_WIDTH});
  height: 100%;
  flex-direction: column;
  margin: 0 auto;
  background: ${COLORS.page};
  box-shadow: 0 0 30px rgba(41, 49, 66, 0.04);
`;

export const Header = styled.header`
  display: flex;
  min-height: 76px;
  flex: 0 0 auto;
  align-items: center;
  gap: 12px;
  padding: 12px 24px;
  background: ${COLORS.white};

  ${MOBILE} {
    min-height: 68px;
    padding: 10px 16px;
  }
`;

export const BackButton = styled.button`
  display: inline-flex;
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  align-items: center;
  justify-content: center;
  border: 1px solid ${COLORS.line};
  border-radius: 50%;
  padding: 0;
  background: ${COLORS.white};
  color: ${COLORS.title};
  cursor: pointer;

  svg {
    width: 24px;
    height: 24px;
    stroke-width: 2;
  }

  &:active {
    transform: scale(0.97);
  }

  ${MOBILE} {
    width: 42px;
    height: 42px;
    flex-basis: 42px;
  }
`;

export const HeaderTitle = styled.h1`
  margin: 0;
  color: ${COLORS.title};
  font-family: ${fontFamilies.bold};
  font-size: 16px;
  line-height: 1.25;

  ${MOBILE} {
    font-size: 15px;
  }
`;

export const SummaryBar = styled.button`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 10px;
  border: 0;
  border-bottom: 1px solid ${COLORS.line};
  padding: 10px 20px;
  background: ${COLORS.white};
  color: ${COLORS.body};
  font-family: ${fontFamilies.regular};
  font-size: 13px;
  cursor: default;
  text-align: left;

  svg {
    flex: 0 0 auto;
    width: 16px;
    height: 16px;
    color: ${COLORS.muted};
  }

  ${MOBILE} {
    padding: 9px 14px;
    font-size: 12px;
  }
`;

export const SummaryText = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;

  strong {
    font-family: ${fontFamilies.semiBold};
    font-size: 13px;
    color: ${COLORS.title};
  }
`;

export const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 20px 24px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${MOBILE} {
    padding: 16px 16px 12px;
  }
`;

export const SectionTitle = styled.h2`
  margin: 0 0 14px;
  color: ${COLORS.title};
  font-family: ${fontFamilies.bold};
  font-size: 18px;
  line-height: 1.25;

  ${MOBILE} {
    font-size: 18px;
    margin-bottom: 12px;
  }
`;

export const InfoBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  border-radius: 12px;
  padding: 11px 14px;
  background: ${COLORS.infoBg};
  color: ${COLORS.body};
  font-size: 13px;
  line-height: 1.45;

  svg {
    flex: 0 0 auto;
    width: 16px;
    height: 16px;
    color: ${COLORS.muted};
  }

  ${MOBILE} {
    font-size: 12px;
  }
`;

export const DateList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const DateSection = styled.div`
  overflow: hidden;
  border-radius: 16px;
  background: ${COLORS.white};
  box-shadow: 0 2px 10px rgba(41, 49, 66, 0.07);
`;

export const DateHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px 14px;

  ${MOBILE} {
    padding: 14px 16px 12px;
  }
`;

export const DateLabel = styled.h3`
  margin: 0;
  color: ${COLORS.title};
  font-family: ${fontFamilies.bold};
  font-size: 16px;
  line-height: 1.25;

  ${MOBILE} {
    font-size: 16px;
  }
`;

export const SlotsFullBadge = styled.span`
  display: inline-flex;
  align-items: center;
  height: 28px;
  border-radius: 8px;
  padding: 0 10px;
  background: ${COLORS.slotsFullBackground};
  color: ${COLORS.slotsFull};
  font-family: ${fontFamilies.semiBold};
  font-size: 13px;
  white-space: nowrap;
`;

export const SlotGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  padding: 0 18px 16px;

  ${MOBILE} {
    gap: 8px;
    padding: 0 16px 14px;
  }
`;

export const SlotButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border: 1.5px solid
    ${(props) => (props.$selected ? COLORS.brand : COLORS.line)};
  border-radius: 12px;
  padding: 11px 14px;
  background: ${COLORS.white};
  color: ${(props) => (props.$disabled ? COLORS.muted : COLORS.title)};
  cursor: ${(props) => (props.$disabled ? "default" : "pointer")};
  opacity: ${(props) => (props.$disabled ? 0.55 : 1)};
  text-align: left;

  &:not([data-disabled]):active {
    transform: scale(0.98);
  }

  ${MOBILE} {
    padding: 10px 12px;
  }
`;

export const SlotTime = styled.span`
  font-family: ${fontFamilies.semiBold};
  font-size: 14px;
  line-height: 1.25;

  ${MOBILE} {
    font-size: 14px;
  }
`;

export const ScheduleExistsLabel = styled.span`
  display: block;
  margin-top: 4px;
  color: #c97c0a;
  font-family: ${fontFamilies.semiBold};
  font-size: 11px;
  line-height: 1.2;
`;

export const ExistingScheduleBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0 0 4px;
  border-radius: 10px;
  padding: 10px 14px;
  background: #fff8ec;
  color: #7a5200;
  font-size: 13px;
  line-height: 1.4;

  svg {
    flex: 0 0 16px;
    width: 16px;
    height: 16px;
    margin-top: 1px;
    color: #c97c0a;
  }

  ${MOBILE} {
    font-size: 12px;
  }
`;

export const SlotRadio = styled.span`
  display: inline-flex;
  width: 20px;
  height: 20px;
  flex: 0 0 20px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 2px solid
    ${(props) => (props.$selected ? COLORS.brand : COLORS.line)};
  background: transparent;

  &::after {
    content: "";
    display: ${(props) => (props.$selected ? "block" : "none")};
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${COLORS.brand};
  }
`;

export const ShowOtherSlotsToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 4px auto 16px;
  border: 0;
  padding: 0;
  background: transparent;
  color: ${COLORS.brand};
  font-family: ${fontFamilies.semiBold};
  font-size: 13px;
  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
    transition: transform 0.2s;
    transform: ${(props) => (props.$open ? "rotate(180deg)" : "none")};
  }

  &:active {
    opacity: 0.8;
  }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
`;

export const SkeletonBlock = styled.div`
  border-radius: 16px;
  background: ${COLORS.white};
  box-shadow: 0 2px 10px rgba(41, 49, 66, 0.07);
  overflow: hidden;
`;

export const SkeletonLine = styled.div`
  width: ${(props) => props.$width || "100%"};
  height: ${(props) => props.$height || "16px"};
  border-radius: 8px;
  background: linear-gradient(90deg, #edf0f5 0%, #f7f8fa 50%, #edf0f5 100%);
  background-size: 200% 100%;
  animation: ${pulse} 1.2s ease-in-out infinite;
`;

export const SkeletonPadding = styled.div`
  padding: 16px 18px 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const warningCardShared = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  border-radius: 16px;
  margin-bottom: 20px;
  padding: 28px 24px;
  background: ${COLORS.white};
  box-shadow: 0 2px 10px rgba(41, 49, 66, 0.07);
  text-align: center;
`;

export const NoSlotsCard = styled.div`
  ${warningCardShared}
`;

export const WarningIconCircle = styled.div`
  display: flex;
  width: 64px;
  height: 64px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${COLORS.warningIconBg};
  color: ${COLORS.warningIcon};
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
`;

export const NoSlotsTitle = styled.h3`
  margin: 2px 0 0;
  color: ${COLORS.title};
  font-family: ${fontFamilies.bold};
  font-size: 16px;
  line-height: 1.25;
`;

export const NoSlotsSubtitle = styled.p`
  margin: 0;
  color: ${COLORS.muted};
  font-size: 13px;
  line-height: 1.5;
`;

export const POCButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 50px;
  width: 100%;
  margin-top: 4px;
  border: 0;
  border-radius: 14px;
  padding: 0 20px;
  background: ${COLORS.brand};
  color: ${COLORS.white};
  font-family: ${fontFamilies.semiBold};
  font-size: 16px;
  text-decoration: none;
  cursor: pointer;

  svg {
    width: 20px;
    height: 20px;
  }

  &:active {
    opacity: 0.9;
  }
`;

export const CtaBar = styled.div`
  flex: 0 0 auto;
  padding: 16px 24px 24px;
  background: ${COLORS.page};

  ${MOBILE} {
    padding: 12px 16px 20px;
  }
`;

// ─── Confirmation modal (bottom sheet) ────────────────────────────────────────

export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(41, 49, 66, 0.55);
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

export const ModalSheet = styled.div`
  width: min(100%, ${PAGE_MAX_WIDTH});
  border-radius: 24px 24px 0 0;
  background: ${COLORS.page};
  padding: 20px 20px 28px;
  max-height: 85dvh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
  align-items: center;

  ${MOBILE} {
    padding: 16px 14px 24px;
  }
`;

export const ModalHandle = styled.div`
  width: 40px;
  height: 4px;
  border-radius: 2px;
  background: ${COLORS.line};
  margin: 0 auto 20px;
`;

export const ModalInfoCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 14px;

  img {
    width: 72px;
    height: 72px;
  }
`;

export const ModalTitle = styled.h2`
  margin: 0 0 20px;
  color: ${COLORS.title};
  font-family: ${fontFamilies.bold};
  font-size: 22px;
  line-height: 1.2;
  text-align: center;

  ${MOBILE} {
    font-size: 20px;
  }
`;

export const ModalSubtitle = styled.p`
  margin: 0 0 20px;
  color: ${COLORS.muted};
  font-size: 14px;
  line-height: 1.5;
  text-align: center;
`;

export const ModalDetailList = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 480px;
  margin-bottom: 20px;
  background: ${COLORS.white};
  border-radius: 16px;
  overflow: hidden;
`;

export const ModalDetailRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 16px;

  &:first-child {
    padding-top: 16px;
  }

  &:last-child {
    padding-bottom: 16px;
  }
`;

export const ModalDetailLabel = styled.span`
  color: ${COLORS.muted};
  font-size: 14px;
  line-height: 1.4;

  ${MOBILE} {
    font-size: 13px;
  }
`;

export const ModalDetailValue = styled.span`
  color: ${COLORS.title};
  font-family: ${fontFamilies.semiBold};
  font-size: 14px;
  line-height: 1.4;
  text-align: right;

  ${MOBILE} {
    font-size: 13px;
  }
`;

export const ModalButtonRow = styled.div`
  display: flex;
  gap: 12px;
  width: calc(100% + 40px);
  margin: 0 -20px -28px;
  padding: 16px 20px 28px;
  background: ${COLORS.white};
  border-radius: 0 0 24px 24px;

  ${MOBILE} {
    gap: 10px;
    width: calc(100% + 28px);
    margin: 0 -14px -24px;
    padding: 14px 14px 24px;
  }
`;

export const ModalCancelButton = styled.button`
  flex: 1;
  min-height: 52px;
  border: 1.5px solid ${COLORS.brand};
  border-radius: 14px;
  padding: 0 16px;
  background: ${COLORS.white};
  color: ${COLORS.brand};
  font-family: ${fontFamilies.semiBold};
  font-size: 16px;
  cursor: pointer;

  &:active {
    opacity: 0.85;
  }
`;

export const ModalConfirmButton = styled.button`
  flex: 1;
  min-height: 52px;
  border: 0;
  border-radius: 14px;
  padding: 0 16px;
  background: ${COLORS.brand};
  color: ${COLORS.white};
  font-family: ${fontFamilies.semiBold};
  font-size: 16px;
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  opacity: ${(props) => (props.disabled ? 0.65 : 1)};

  &:not(:disabled):active {
    opacity: 0.9;
  }
`;

// ─── Success screen ────────────────────────────────────────────────────────────

export const SuccessShell = styled.main`
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  background: ${COLORS.page};
  color: ${COLORS.title};
  font-family: ${fontFamilies.regular};
`;

export const SuccessFrame = styled.div`
  display: flex;
  width: min(100%, ${PAGE_MAX_WIDTH});
  height: 100%;
  flex-direction: column;
  margin: 0 auto;
  background: ${COLORS.page};
  box-shadow: 0 0 30px rgba(41, 49, 66, 0.04);
`;

export const SuccessScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 24px 24px;
  gap: 6px;
  background: ${COLORS.page};

  ${MOBILE} {
    padding: 32px 16px 20px;
  }
`;

export const SuccessIconCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;

  img {
    width: 80px;
    height: 80px;
  }
`;

export const SuccessHeadline = styled.h1`
  margin: 0 0 4px;
  color: ${COLORS.title};
  font-family: ${fontFamilies.bold};
  font-size: 22px;
  line-height: 1.2;
  text-align: center;

  ${MOBILE} {
    font-size: 20px;
    margin-bottom: 4px;
  }
`;

export const SuccessCount = styled.span``;

export const SuccessSubheadline = styled.p`
  margin: 0 0 20px;
  color: ${COLORS.muted};
  font-size: 15px;
  line-height: 1.5;
  text-align: center;
`;

export const SuccessDetailList = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 480px;
  margin-bottom: 24px;
  background: ${COLORS.white};
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(41, 49, 66, 0.09);
  overflow: hidden;
`;

export const SuccessDetailRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 16px;

  &:first-child {
    padding-top: 16px;
  }

  &:last-child {
    padding-bottom: 16px;
  }
`;

export const SuccessDetailLabel = styled(ModalDetailLabel)``;
export const SuccessDetailValue = styled(ModalDetailValue)``;

export const SuccessCtaBar = styled(CtaBar)`
  background: ${COLORS.white};
`;

export const SuccessHeader = styled(Header)`
  justify-content: space-between;
`;

export const SuccessCtaRow = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;

  ${MOBILE} {
    gap: 10px;
  }
`;

export const AddInvoicesButton = styled.button`
  flex: 1;
  min-height: 56px;
  border: 1.5px solid ${COLORS.brand};
  border-radius: 16px;
  padding: 0 16px;
  background: ${COLORS.white};
  color: ${COLORS.brand};
  font-family: ${fontFamilies.semiBold};
  font-size: 16px;
  cursor: pointer;

  &:active {
    opacity: 0.85;
  }

  ${MOBILE} {
    min-height: 50px;
    font-size: 15px;
  }
`;

export const ViewScheduleButton = styled.button`
  flex: 1;
  min-height: 56px;
  border: 0;
  border-radius: 16px;
  padding: 0 16px;
  background: ${COLORS.brand};
  color: #ffffff;
  font-family: ${fontFamilies.semiBold};
  font-size: 16px;
  cursor: pointer;

  &:active {
    opacity: 0.92;
    transform: scale(0.99);
  }

  ${MOBILE} {
    min-height: 50px;
    font-size: 15px;
  }
`;

export const SuccessScheduleNumber = styled.p`
  margin: 0 0 8px;
  color: ${COLORS.muted};
  font-family: ${fontFamilies.semiBold};
  font-size: 15px;
  line-height: 1.4;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;

export const SuccessShareButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: 1.5px solid ${COLORS.line};
  border-radius: 50%;
  background: ${COLORS.white};
  color: ${COLORS.title};
  cursor: pointer;

  svg {
    width: 18px;
    height: 18px;
  }

  &:active {
    opacity: 0.7;
  }
`;

// ─── Existing ─────────────────────────────────────────────────────────────────

export const ScheduleButton = styled.button`
  width: 100%;
  min-height: 56px;
  border: 0;
  border-radius: 16px;
  padding: 0 24px;
  background: ${(props) => (props.disabled ? "#e8e9ed" : COLORS.brand)};
  color: ${(props) => (props.disabled ? "#9ba2b3" : "#ffffff")};
  font-family: ${fontFamilies.semiBold};
  font-size: 18px;
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};

  &:not(:disabled):active {
    opacity: 0.92;
    transform: scale(0.99);
  }

  ${MOBILE} {
    min-height: 50px;
    font-size: 16px;
  }
`;


export const ModalIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${COLORS.white};
  margin: 0 auto 14px;
`;
